import { useMemo, useState } from "react";
import type { ReferralDashboardEntry } from "@/utils/api/referrals";
import type { SortDirection, SortField } from "../types";

/**
 * Hook for managing table sorting state and logic
 */
export const useSorting = (entries?: ReferralDashboardEntry[]) => {
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const sortedEntries = useMemo(() => {
    if (!entries) return [];

    const entriesCopy = [...entries];

    entriesCopy.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "date":
          comparison =
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime();
          break;
        case "email":
          // Sort by name if available, fallback to email
          const aDisplay = a.refereeName || a.refereeEmail || "";
          const bDisplay = b.refereeName || b.refereeEmail || "";
          comparison = aDisplay.localeCompare(bDisplay);
          break;
        case "signupSource":
          comparison = (a.signupSource || "").localeCompare(
            b.signupSource || ""
          );
          break;
        case "paymentStatus":
          comparison = (a.paymentStatus || "").localeCompare(
            b.paymentStatus || ""
          );
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return entriesCopy;
  }, [entries, sortField, sortDirection]);

  return {
    sortField,
    sortDirection,
    sortedEntries,
    handleSort,
  };
};
